import { axiosService } from "@/services/AxiosService";

/**
 * @typedef {Object} Lesson
 * @property {string} horse_id- ID del cavallo
 * @property {string} groom_id - Nome del cavallo
 * @property {string} riding_stable_id - Soprannome del cavallo (facoltativo)
 * @property {string} lesson_type_id - Codice microchip del cavallo
 * @property {string} student_id - Passaporto APA (facoltativo)
 * @property {string} field_id - Data di scadenza del passaporto APA (facoltativo)
 * @property {string} note - Passaporto FISE (facoltativo)
 * @property {string} level - Data di scadenza del passaporto FISE (facoltativo)
 * @property {string} description - Passaporto FEI (facoltativo)
 * @property {string} title - Data di scadenza del passaporto FEI (facoltativo)
 * @property {number} end_date - ID della razza
 * @property {number} start_date - ID del colore
 * @property {string} days_of_week - Data di nascita del cavallo
 * @property {string} end_time - Nome del padre del cavallo
 * @property {string} start_time - Nome della madre del cavallo

 */
/**
 * @typedef {object} LessonType
 * @property {string} name
 * @property {number} id
 */
/**
 * @typedef {object} JoinData
 * @property {number} user_id
 * @property {number} riding_stable_id
 */
/**
 * @typedef {object} RidingStable
 * @property {number} id
 * @property {string} name
 * @property {string} address
 * @property {number} fiscal_data_id
 * @property {string} created
 * @property {string} modified
 * @property {JoinData} _joinData
 */
/**
 * @typedef {object} Groom
 * @property {number} id
 * @property {string} username
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} email
 *
 */
/**
 * @typedef {object} Student
 * @property {number} id - The unique ID of the user.
 * @property {string} first_name - The first name of the user.
 * @property {string} last_name - The last name of the user.
 * @property {string} username - The username of the user.
 * @property {string} email - The email address of the user.
 * @property {string} phone_number - The phone number of the user.
 * @property {string} location - The location of the user.
 * @property {string} photo_url - The URL of the user's profile photo.
 * @property {string} profession - The profession of the user.
 * @property {number} role_id - The role ID of the user.
 * @property {boolean} deleted - Whether the user is marked as deleted.
 * @property {null|string} deleted_data - Additional data about deletion (if any).
 * @property {string} created - The timestamp when the user was created (ISO format).
 * @property {string} modified - The timestamp when the user was last modified (ISO format).
 */
/**
 * @typedef {object} Horse
 * @property {number} id - The unique identifier for the horse.
 * @property {string} name - The name of the horse.
 * @property {string} nickname - The nickname of the horse.
 * @property {string} microchip - The microchip number of the horse.
 * @property {string} foaling_date - The foaling date of the horse (ISO 8601 date format).
 * @property {number} breed_id - The breed identifier for the horse.
 * @property {number} colour_id - The colour identifier for the horse.
 * @property {number} genre_id - The genre identifier for the horse.
 * @property {string} sex - The sex of the horse (e.g., "M" for male, "F" for female).
 * @property {number|null} id_stable - The stable identifier where the horse is kept (nullable).
 * @property {number} height - The height of the horse.
 * @property {string} father - The name of the horse's father.
 * @property {string} mother - The name of the horse's mother.
 * @property {string} grandparent - The name of the horse's grandparent.
 * @property {string} fise_passport - The FISE passport number for the horse.
 * @property {string|null} fise_passport_expiration - The expiration date of the FISE passport (nullable).
 * @property {string} fei_passport - The FEI passport number for the horse.
 * @property {string|null} fei_passport_expiration - The expiration date of the FEI passport (nullable).
 * @property {string} apa_passport - The APA passport number for the horse.
 * @property {string|null} apa_passport_expiration - The expiration date of the APA passport (nullable).
 * @property {boolean} suspended - Whether the horse is suspended.
 * @property {string} created - The creation timestamp of the horse record (ISO 8601 date format).
 * @property {string} modified - The last modified timestamp of the horse record (ISO 8601 date format).
 * @property {number|null} cost - The cost of the horse (nullable).
 * @property {boolean} school - Whether the horse is used for school purposes.
 */
/**
 * @typedef {object} Field
 * @property {number} id - The unique identifier for the field.
 * @property {string|null} description - A description of the field (nullable).
 * @property {string} name - The name of the field.
 * @property {number} riding_stable_id - The identifier of the riding stable associated with the field.
 * @property {string} created - The creation timestamp of the field record (ISO 8601 date format).
 * @property {string} modified - The last modified timestamp of the field record (ISO 8601 date format).
 */

class LessonsService {
  abortController = null;

  constructor() {
    this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
  }

  /**
   * Recupera la lista dei cavalli.
   * @returns {Promise<Lesson[]>} - Una promessa che risolve con la lista di cavalli
   * @throws {Error} - Lancia un errore se la chiamata fallisce
   */

  async getStudents(search = "") {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    try {
      const response = await this.axiosInstance.get("/lessons/get-students", {
        params: { search },
        signal,
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch users info");
      }

      // /** @type {User[]} */
      
      return response.data.data;
    } catch (error) {
      console.error("Error fetching users info:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
  async getHorses(student_id, riding_stable_id) {
    try {
      const response = await this.axiosInstance.get("/lessons/get-horses", {
        params: { student_id, riding_stable_id },
      });
      if (response.status !== 200) {
        throw new Error("Failed to fetch horses info");
      }
      
      return response.data.data;
    } catch (error) {
      console.error("Error fetching horses info", error);
    }
  }

  async getInstructors() {
    try {
      const response = await this.axiosInstance.get("/lessons/get-instructors");
      if (response.status !== 200) {
        throw new Error("Failed to fetch instructors info");
      }
      return response.data.data;
    } catch (error) {
      console.error("Error fetching instructors info", error);
    }
  }

  /**
   *
   * @returns {Promise<Student[]>}
   */

  async getLessonTypes() {
    try {
      const response = await this.axiosInstance.get("/lessons/get-types");

      if (response.status !== 200) {
        throw new Error("Failed to fetch lesson types");
      }

      /** @type {LessonType[]} */
      return response.data?.lesson_types;
    } catch (error) {
      console.error("Error fetching lesson types:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
  async getRidingStables() {
    try {
      const response = await this.axiosInstance.get(
        "/lessons/get-riding-stables"
      );

      if (response.status !== 200) {
        throw new Error("Failed to fetch riding stables");
      }

      /** @type {RidingStable[]} */
      return response.data?.riding_stables;
    } catch (error) {
      console.error("Error fetching riding stables:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
  async getGrooms() {
    try {
      const response = await this.axiosInstance.get("/lessons/get-grooms");

      if (response.status !== 200) {
        throw new Error("Failed to fetch grooms");
      }

      /** @type {Groom[]} */
      return response.data?.grooms;
    } catch (error) {
      console.error("Error fetching grooms:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
  async getFields(riding_stable_id) {
    try {
      const response = await this.axiosInstance.get("/lessons/get-fields", {
        params: { riding_stable_id },
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch fields");
      }

      /** @type {Field[]} */
      
      return response.data.data;
    } catch (error) {
      console.error("Error fetching fields:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }

  async save(data) {
    try {
      const response = await this.axiosInstance.post("/lessons/save", data);
      if (response.status !== 200) {
        throw new Error("Failed to save lesson data");
      }
      /** @type {Lesson} */
      return response.data.data;
    } catch (error) {
      console.error("Error saving lesson:", error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }

  async list(page, search = '', params = {}, length = 10) {
    try {
        const response = await this.axiosInstance.get('/lessons/list', {params: {page, length, search, ...params}});

        if (response.status !== 200) {
            throw new Error('Failed to fetch lessons info');
        }

        /** @type {Lesson[]} */
        return response.data;
    } catch (error) {
        console.error('Error fetching lesson info:', error);
        throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }

  async get(id) {
    try {
        const response = await this.axiosInstance.get('/lessons/get?id=' + id);

        if (response.status !== 200) {
            throw new Error('Failed to fetch lesson info');
        }

        /** @type {Lesson} */
        return response.data.data;
    } catch (error) {
        console.error('Error fetching lesson info:', error);
        throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }

  async delete(id) {
    try {
        const response = await this.axiosInstance.post('/lessons/delete', {id});
        if (response.status !== 200) {
            throw new Error('Failed to delete lesson info');
        }
        return true;
    } catch (error) {
        console.error('Error deleteing lesson info:', error);
        throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
  async getCalendarLessons(startDate,endDate, filters) {
    try {
      const response = await this.axiosInstance.get(
        "/lessons/get-month-lessons",
        { params: { start_date: startDate, end_date: endDate, ...filters } }
      );
      if (response.status !== 200) throw new Error("Failed to fetch lessons");

      
      return response.data.lessons
    } catch (error) {
      console.error('Error fetching lessons info:', error);
      throw error; // Rialza l'errore per gestirlo nel chiamante
    }
  }
}

export const lessonsService = new LessonsService();
